import type { GQL } from "@binale-tech/shared";

import type { MessageId } from "@domain/intl/useTranslation";

import type {
    AgentContext,
    AgentIO,
    AgentRunControl,
    AgentRunReport,
    AgentStep,
    AgentStepId,
    AgentStepResult,
} from "./types";

export const MIN_THINKING_MS = 800;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** Shows a thinking line while `work` runs and keeps it at least MIN_THINKING_MS. */
export const withThinking = async <T>(
    io: Pick<AgentIO, "post" | "update">,
    textId: MessageId,
    work: () => Promise<T>,
): Promise<T> => {
    const id = io.post({ kind: "thinking", textId });
    const started = Date.now();
    try {
        return await work();
    } finally {
        const rest = MIN_THINKING_MS - (Date.now() - started);
        if (rest > 0) await sleep(rest);
        io.update(id, { kind: "done" });
    }
};

const dependencyMet = (step: AgentStep, ctx: AgentContext, report: AgentRunReport): boolean => {
    const { needs } = step;
    if (needs === undefined) return true;
    if (typeof needs === "function") return needs(ctx);
    return needs.every((id) => {
        const status = report.steps[id]?.status;
        return status === "done" || status === "nothing";
    });
};

const record = (report: AgentRunReport, id: AgentStepId, result: AgentStepResult): void => {
    report.steps[id] = {
        status: result.status,
        summary: result.summary ?? [],
        open: result.open ?? [],
        ...(result.data?.continuesOnServer === true ? { continuesOnServer: true as const } : {}),
    };
};

/** Walks the steps in order; rights, dependencies, pause, close and errors are handled here. */
export const runAgent = async (
    steps: AgentStep[],
    ctx: AgentContext,
    runIo: Omit<AgentIO, "narrateSkipped">,
    control: AgentRunControl,
): Promise<AgentRunReport> => {
    const report: AgentRunReport = { steps: {}, notRun: [] };
    ctx.runReport = report;
    const narratedProducts = new Set<GQL.IProductKey>();
    const narrateSkipped = (product: GQL.IProductKey) => {
        if (narratedProducts.has(product)) return;
        narratedProducts.add(product);
        runIo.post({
            kind: "skipped",
            textId: "app.agent.skippedNoRight",
            labels: { product: `app.titles.${product}` },
        });
    };
    const io: AgentIO = { ...runIo, narrateSkipped };

    for (const [index, step] of steps.entries()) {
        await control.waitIfPaused();
        if (control.isCancelled()) {
            report.notRun = steps.slice(index).map((s) => s.id);
            break;
        }

        const missing = (step.product ?? []).filter((p) => !ctx.canWrite(p));
        if (missing.length > 0) {
            for (const product of missing) narrateSkipped(product);
            record(report, step.id, { status: "skipped" });
            continue;
        }

        if (!dependencyMet(step, ctx, report)) {
            io.post({
                kind: "skipped",
                textId: "app.agent.skippedDependency",
                labels: { step: `app.agent.step.${step.id}` },
            });
            record(report, step.id, { status: "skipped" });
            continue;
        }

        let result: AgentStepResult;
        try {
            result = await step.run(ctx, io);
        } catch (error) {
            console.error(`Agent step ${step.id} failed`, error);
            io.post({ kind: "error", textId: "app.agent.stepFailed", labels: { step: `app.agent.step.${step.id}` } });
            result = { status: "failed" };
        }
        record(report, step.id, result);
        if (result.status === "nothing") {
            io.post({ kind: "done", textId: "app.agent.nothingToDo", labels: { step: `app.agent.step.${step.id}` } });
        }
        if (control.isCancelled() && result.data?.continuesOnServer === true) {
            io.post({ kind: "text", textId: "app.agent.continuesOnServer" });
        }
    }
    return report;
};
